/**
 * TanStack Router — Route Tree
 *
 * Code-based route tree for the Switchboard UI.
 * Auth guarding via beforeLoad, lazy components via lazy().
 */

import {
  createRouter,
  createRootRouteWithContext,
  createRoute,
  redirect,
  Outlet,
  lazyRouteComponent,
  type ErrorComponentProps,
  type ParsedLocation,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import type { AdminRole, AdminUser } from './types/admin';
import { hasMinimumRole, hasPermission } from './types/admin';
import { ADMIN_ROUTE_ACCESS } from './auth/routeAccess';
import { notFoundUnlessBillingVisible, notFoundUnlessPurchasesEnabled, guardPublicPurchasePages } from './auth/purchaseGuards';
import { RouteErrorComponent } from './components/shared/RouteErrorComponent';
import { RoutePendingComponent } from './components/shared/RoutePendingComponent';
import { NotFoundPage } from './components/shared/NotFoundPage';
import { UnauthorizedPage } from './components/shared/UnauthorizedPage';
import { Toaster } from './components/ui/sonner';
import PWAPrompt from './components/shared/PWAPrompt';
import { useRateLimitHandler } from './hooks/useRateLimitHandler';
import { safeRedirect } from './auth/safeRedirect';
import { positiveInt, str } from './navigation/searchParams';
import { parseSearch, stringifySearch } from './navigation/searchSerialization';
import { useAppDocumentTitle } from './navigation/useDocumentTitle';
import { adminLayoutSearch } from './components/admin/AdminLayout.search';
import { loginSearch } from './pages/admin/LoginPage.search';
import { agentsSearch } from './pages/admin/AgentsPage.search';
import { usersSearch } from './pages/admin/UsersPage.search';
import { voiceConfigSearch } from './pages/admin/VoiceConfigPage.search';
import { emailTemplatesSearch } from './pages/admin/EmailTemplatesPage.search';
import { evaluationSearch } from './pages/admin/EvaluationPage.search';
import { audioMonitorSearch } from './pages/admin/AudioMonitorPage.search';
import { outboundCallsSearch } from './pages/admin/OutboundCallsPage.search';
import { conversationsSearch } from './pages/admin/ConversationsPage.search';
import { integrationsSearch } from './pages/admin/IntegrationsPage.search';
import { phoneNumbersSearch } from './pages/admin/PhoneNumbersPage.search';
import { auditLogsSearch } from './pages/admin/AuditLogsPage.search';
import { developerApiSearch } from './pages/admin/DeveloperApiPage.search';
import { usageSearch } from './pages/admin/UsagePage.search';
import { dashboardSearch } from './pages/admin/AdminDashboardPage.search';
import { topupSearch } from './pages/admin/TopupPage.search';
import { billingSearch } from './pages/admin/BillingPage.search';
import { unauthorizedSearch } from './pages/admin/UnauthorizedPage.search';
import { platformUsageSearch } from './pages/admin/platform/PlatformUsagePage.search';
import { platformCallQualitySearch } from './pages/admin/platform/PlatformCallQualityPage.search';
import { platformTenantSearch } from './pages/admin/platform/PlatformTenantPage.search';
import { platformTenantUsageSearch } from './pages/admin/platform/PlatformTenantUsagePage.search';

// ---------------------------------------------------------------------------
// Router context type
// ---------------------------------------------------------------------------

export interface RouterContext {
  queryClient: QueryClient;
  auth: {
    isAuthenticated: boolean;
    isAuthenticatedRef: { current: boolean };
    loading: boolean;
    user: AdminUser | null;
    userRef: { current: AdminUser | null };
    ready: Promise<boolean>;
  };
}

// ---------------------------------------------------------------------------
// Root route — renders shell + global UI
// ---------------------------------------------------------------------------

function RootComponent() {
  useRateLimitHandler();
  // The pages outside the admin layout (sign-in, chat, sign-up…) name the tab here.
  useAppDocumentTitle();
  return (
    <>
      <Outlet />
      <Toaster />
      <PWAPrompt />
    </>
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
});

// ---------------------------------------------------------------------------
// Auth guard
// ---------------------------------------------------------------------------

/** What every guard receives: the router context and, from TanStack's beforeLoad, where the user is going. */
interface GuardArgs {
  context: RouterContext;
  location?: ParsedLocation;
}

/**
 * The login page, carrying where the user was going so signing in lands
 * there (only a same-origin admin page; `safeRedirect` decides).
 */
function toLogin(location: ParsedLocation | undefined) {
  const target = location ? safeRedirect(location.href) : null;
  return redirect({ to: '/admin/login', search: target ? { redirect: target } : {} });
}

async function requireAuth({ context, location }: GuardArgs) {
  if (context.auth.loading) {
    const ready = await context.auth.ready;
    if (!ready) {
      throw toLogin(location);
    }
    return;
  }
  // Use the synchronous ref — React state may be stale between setUser() and commit
  if (!context.auth.isAuthenticatedRef.current) {
    // Local login is the default entry. The page itself offers SwiftGate as
    // an alternative button, one click away.
    throw toLogin(location);
  }
}

function requireMinimumRole(min: AdminRole) {
  return async ({ context, location }: GuardArgs) => {
    await requireAuth({ context, location });
    // Read from the ref — same rationale as isAuthenticatedRef in requireAuth:
    // context.auth.user is a snapshot captured before await, and may still be
    // null between setUser() and React's commit even though auth.ready has
    // resolved. The ref is updated synchronously in checkSession.
    const user = context.auth.userRef.current;
    if (!user || !hasMinimumRole(user.role, min)) {
      throw redirect({ to: '/admin/unauthorized', search: { required: min } });
    }
  };
}

/**
 * Livepoint's platform admin: a grant the session reports as
 * `platform_admin`, never a tenant role. Same stale-state rationale as the
 * role guard. Redirects to the 403 panel via `?forbidden=1` without naming
 * what was required.
 */
function requirePlatformAdmin() {
  return async ({ context, location }: GuardArgs) => {
    await requireAuth({ context, location });
    const user = context.auth.userRef.current;
    if (!user?.platform_admin) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router redirects are thrown
      throw redirect({ to: '/admin/unauthorized', search: { forbidden: '1' } });
    }
  };
}

/**
 * Permission-tier sibling of `requireMinimumRole` — gatekeeps a route on a
 * `resource:action` rather than a role tier. Same stale-state rationale: read
 * the synchronously-correct `userRef`, not the pre-await `context.auth.user`.
 * Redirects to the 403 panel via `?forbidden=1` (there is no single role tier
 * to name for a permission denial, unlike the role-guard's `?required=`).
 */
function requireMinimumPermission(resource: string, action: string) {
  return async ({ context, location }: GuardArgs) => {
    await requireAuth({ context, location });
    const user = context.auth.userRef.current;
    if (!user || !hasPermission(user.permissions, resource, action)) {
      throw redirect({ to: '/admin/unauthorized', search: { forbidden: '1' } });
    }
  };
}

/**
 * Build a route guard from the single source of truth in `auth/routeAccess.ts`.
 * The sidebar reads the SAME map to gate its nav items, so the route guard and
 * the nav affordance can't drift — and neither can drift from what the backend
 * enforces. Dispatches to `requireMinimumRole` (role-tier surfaces) or
 * `requireMinimumPermission` (resource:action surfaces); a path with no entry
 * falls back to bare `requireAuth`.
 */
function requireRouteAccess(path: string) {
  const access = ADMIN_ROUTE_ACCESS[path];
  // requireRouteAccess is only ever called for a GATED route, so a missing
  // entry means a typo'd path that would silently downgrade to authenticated-
  // only access. Fail loudly at module-eval (the router-construction test trips
  // on it) rather than shipping a route with no real guard.
  if (!access) {
    throw new Error(`requireRouteAccess: no ADMIN_ROUTE_ACCESS entry for "${path}"`);
  }
  if (access.platformAdmin) return requirePlatformAdmin();
  if (access.minimumRole) return requireMinimumRole(access.minimumRole);
  if (access.permission) return requireMinimumPermission(access.permission.resource, access.permission.action);
  return requireAuth;
}

/**
 * Mirror of `requireAuth` for routes that should only show to anonymous
 * users (like `/admin/login`). When the visitor is already authenticated,
 * bounce them to `/admin` instead of forcing them to log in again.
 *
 * Carve-outs (do NOT redirect):
 *   - `?error=…`         — the user just bounced back from a failed SwiftGate
 *                          callback; surface the error message instead of
 *                          silently swallowing it with an `/admin` redirect.
 *   - `?logged_out=true` — the user explicitly just logged out; bouncing them
 *                          back into the app would be the opposite of what
 *                          they asked for. Their session SHOULD be dead by
 *                          this point, but defend in case of a cookie race.
 *   - `?reset_success=…` — they just finished a password reset and we want
 *                          them to see the confirmation + log in fresh.
 */
async function redirectIfAuthenticated({ context, search }: { context: RouterContext; search: { error?: string; logged_out?: boolean; reset_success?: string; redirect?: string } }) {
  if (search.error || search.logged_out || search.reset_success) return;
  // Already signed in: go where the link was going, if it is safe to.
  const target = safeRedirect(search.redirect);
  const home = () => (target ? redirect({ href: target }) : redirect({ to: '/admin' }));
  if (context.auth.loading) {
    const ready = await context.auth.ready;
    if (ready) throw home();
    return;
  }
  if (context.auth.isAuthenticatedRef.current) {
    throw home();
  }
}

// ---------------------------------------------------------------------------
// Index — redirect to /admin
// ---------------------------------------------------------------------------

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/admin' });
  },
});

// ---------------------------------------------------------------------------
// Login (public) — local email/password + "Continue with SwiftGate" button.
// The previous auto-redirect-to-SwiftGate has been removed in favour of an
// explicit user choice. Errors from the SwiftGate callback flow still land
// here with ?error=... and are surfaced by LoginPage.
// ---------------------------------------------------------------------------

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/login',
  validateSearch: loginSearch,
  beforeLoad: redirectIfAuthenticated,
  component: lazyRouteComponent(() => import('./pages/admin/LoginPage')),
});

// ---------------------------------------------------------------------------
// Chat (protected, typed search params)
// ---------------------------------------------------------------------------

const chatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/chat',
  beforeLoad: requireAuth,
  validateSearch: z.object({ agent: str }),
  component: lazyRouteComponent(() => import('./pages/chat/ChatPage')),
});

// ---------------------------------------------------------------------------
// Admin layout (protected — all children inherit this guard)
// ---------------------------------------------------------------------------

const adminLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  beforeLoad: requireAuth,
  validateSearch: adminLayoutSearch,
  component: lazyRouteComponent(() => import('./components/admin/AdminLayout')),
});

// Admin child routes

/**
 * Spread into every admin child route: a page that is loading or has crashed
 * shows its spinner or error in the layout's outlet, so the header and
 * sidebar stay. TanStack does not inherit these from the parent route, and
 * the router defaults (full-screen) still cover the routes outside the frame.
 */
const inAdminFrame = {
  pendingComponent: () => <RoutePendingComponent inFrame />,
  errorComponent: (props: ErrorComponentProps) => <RouteErrorComponent {...props} inFrame />,
};

// /admin/ is the landing page (was the dashboard previously). Dashboard now
// lives at /admin/dashboard, reachable from the sidebar.
const adminIndexRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/',
  component: lazyRouteComponent(() => import('./pages/admin/LandingPage')),
});

const adminDashboardRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/dashboard',
  validateSearch: dashboardSearch,
  component: lazyRouteComponent(() => import('./pages/admin/AdminDashboardPage')),
});

const adminUsersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/users',
  beforeLoad: requireRouteAccess('/admin/users'),
  validateSearch: usersSearch,
  component: lazyRouteComponent(() => import('./pages/admin/UsersPage')),
});

const adminAgentsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/agents',
  beforeLoad: requireRouteAccess('/admin/agents'),
  validateSearch: agentsSearch,
  component: lazyRouteComponent(() => import('./pages/admin/AgentsPage')),
});

const adminVoiceConfigsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/voice-configs',
  beforeLoad: requireRouteAccess('/admin/voice-configs'),
  validateSearch: voiceConfigSearch,
  component: lazyRouteComponent(() => import('./pages/admin/VoiceConfigPage')),
});

const adminEmailTemplatesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/email-templates',
  beforeLoad: requireRouteAccess('/admin/email-templates'),
  validateSearch: emailTemplatesSearch,
  component: lazyRouteComponent(() => import('./pages/admin/EmailTemplatesPage')),
});

// Analytics was merged into the Dashboard. Kept as a redirect so bookmarks
// and old links land on the page that replaced it.
const adminAnalyticsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/analytics',
  beforeLoad: () => {
    throw redirect({ to: '/admin/dashboard' });
  },
});

const adminEvaluationRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/evaluation',
  // Platform-only: every /admin/evaluation API is platform-admin-only (in-memory
  // singletons, no tenant tag). Guard so a tenant admin gets the clean
  // /admin/unauthorized redirect instead of a page that 403s on every call.
  beforeLoad: requireRouteAccess('/admin/evaluation'),
  validateSearch: evaluationSearch,
  component: lazyRouteComponent(() => import('./pages/admin/EvaluationPage')),
});

const adminAudioMonitorRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/audio-monitor',
  // Audio monitoring is global/cross-tenant — every /admin/audio API is
  // platform-admin-only. Guard the route so anyone else gets the clean
  // /admin/unauthorized redirect instead of a page that soft-fails on 403s.
  beforeLoad: requireRouteAccess('/admin/audio-monitor'),
  validateSearch: audioMonitorSearch,
  component: lazyRouteComponent(() => import('./pages/admin/AudioMonitorPage')),
});

const adminOutboundCallsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/outbound-calls',
  beforeLoad: requireRouteAccess('/admin/outbound-calls'),
  validateSearch: outboundCallsSearch,
  component: lazyRouteComponent(() => import('./pages/admin/OutboundCallsPage')),
});

const adminConversationsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/conversations',
  beforeLoad: requireRouteAccess('/admin/conversations'),
  validateSearch: conversationsSearch,
  component: lazyRouteComponent(() => import('./pages/admin/ConversationsPage')),
});

const adminIntegrationsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/integrations',
  beforeLoad: requireRouteAccess('/admin/integrations'),
  validateSearch: integrationsSearch,
  component: lazyRouteComponent(() => import('./pages/admin/IntegrationsPage')),
});

const adminPhoneNumbersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/phone-numbers',
  beforeLoad: requireRouteAccess('/admin/phone-numbers'),
  validateSearch: phoneNumbersSearch,
  component: lazyRouteComponent(() => import('./pages/admin/PhoneNumbersPage')),
});

const adminAuditLogsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/audit-logs',
  beforeLoad: requireRouteAccess('/admin/audit-logs'),
  validateSearch: auditLogsSearch,
  component: lazyRouteComponent(() => import('./pages/admin/AuditLogsPage')),
});

// The Developer API page: the organisation's customer API keys and how to
// use them. api_key:read (operators view, admins manage; viewers never reach it).
const adminApiKeysRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/api-keys',
  beforeLoad: requireRouteAccess('/admin/api-keys'),
  validateSearch: developerApiSearch,
  component: lazyRouteComponent(() => import('./pages/admin/DeveloperApiPage')),
});

const adminSystemRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/system',
  beforeLoad: requireRouteAccess('/admin/system'),
  component: lazyRouteComponent(() => import('./pages/admin/SystemPage')),
});

const adminSettingsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/settings',
  component: lazyRouteComponent(() => import('./pages/admin/SettingsPage')),
});

// /admin/welcome is now the landing page at /admin/. Kept as a redirect alias
// so existing bookmarks and the post-signup server-side redirect still work.
const adminWelcomeRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/welcome',
  beforeLoad: () => {
    throw redirect({ to: '/admin' });
  },
});

const adminWalkthroughRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/walkthroughs/$slug',
  component: lazyRouteComponent(() => import('./pages/admin/WalkthroughPage')),
});

/**
 * The billing pages (/admin/usage, /admin/billing, /admin/usage/topup) exist
 * only for a charged tenant (session `billing_visible`): anyone else gets the
 * ordinary not-found page, checked before the role so a tenant that is not
 * charged never learns the page exists. Then `requireMinimumRole(min)`.
 */
function requireBillingVisible(min: AdminRole) {
  const inner = requireMinimumRole(min);
  return async ({ context, location }: GuardArgs) => {
    notFoundUnlessBillingVisible(context.auth.userRef.current);
    await inner({ context, location });
  };
}

const adminUsageRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/usage',
  beforeLoad: requireBillingVisible('operator'),
  // A top-up started here returns here from Stripe Checkout (usageSearch takes its return params).
  validateSearch: usageSearch,
  component: lazyRouteComponent(() => import('./pages/admin/UsagePage')),
});

/**
 * `requireBillingVisible(min)` plus the purchasing switch: the top-up and
 * billing pages exist only while the session carries `purchases_enabled`
 * (server canBuyCredit: tenant charged, CREDIT_PURCHASES_ENABLED on and the
 * tenant's own purchase switch on). Otherwise they show the
 * ordinary not-found page, checked before the role like billing visibility, so
 * nobody learns of a page that does not exist; while purchasing is on, a
 * below-tier user gets the honest 403 panel.
 */
function requirePurchasesEnabled(min: AdminRole) {
  const inner = requireBillingVisible(min);
  return async ({ context, location }: GuardArgs) => {
    notFoundUnlessPurchasesEnabled(context.auth.userRef.current);
    await inner({ context, location });
  };
}

const adminUsageTopupRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/usage/topup',
  beforeLoad: requirePurchasesEnabled('admin'),
  validateSearch: topupSearch,
  component: lazyRouteComponent(() => import('./pages/admin/TopupPage')),
});

const adminBillingRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/billing',
  beforeLoad: requirePurchasesEnabled('admin'),
  // Stripe Checkout and the card portal both return here.
  validateSearch: billingSearch,
  component: lazyRouteComponent(() => import('./pages/admin/BillingPage')),
});

// Livepoint's view of every tenant's usage and the per-tenant credit tool.
// Every /api/admin/platform route is platform-admin-only (platform.ts).
const adminPlatformUsageRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/platform/usage',
  beforeLoad: requireRouteAccess('/admin/platform/usage'),
  validateSearch: platformUsageSearch,
  component: lazyRouteComponent(() => import('./pages/admin/platform/PlatformUsagePage')),
});

// The daily call-quality report: every tenant's agents, per UTC day.
const adminPlatformCallQualityRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/platform/call-quality',
  beforeLoad: requireRouteAccess('/admin/platform/call-quality'),
  validateSearch: platformCallQualitySearch,
  component: lazyRouteComponent(() => import('./pages/admin/platform/PlatformCallQualityPage')),
});

// One tenant's usage, read-only: the cost breakdown, linking to its account.
const adminPlatformTenantUsageRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/platform/usage/$tenantId',
  beforeLoad: requireRouteAccess('/admin/platform/usage/$tenantId'),
  // The period, kept from the tenant list it was opened from.
  validateSearch: platformTenantUsageSearch,
  // The page takes the tenant as a prop.
  component: lazyRouteComponent(() =>
    import('./pages/admin/platform/PlatformTenantUsagePage').then(({ default: PlatformTenantUsagePage }) => ({
      default: function PlatformTenantUsageRouteComponent() {
        const { tenantId } = adminPlatformTenantUsageRoute.useParams();
        return <PlatformTenantUsagePage tenantId={tenantId} />;
      },
    })),
  ),
});

// Livepoint's list of tenant accounts, each opening its account page.
const adminPlatformTenantsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/platform/tenants',
  beforeLoad: requireRouteAccess('/admin/platform/tenants'),
  component: lazyRouteComponent(() => import('./pages/admin/platform/PlatformTenantsPage')),
});

// One tenant's account: its credit, ledger and settings, each change in a dialog.
const adminPlatformTenantRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/platform/tenants/$tenantId',
  beforeLoad: requireRouteAccess('/admin/platform/tenants/$tenantId'),
  // The ledger's grouping and paging, and the account's edit dialogs.
  validateSearch: platformTenantSearch,
  // The page takes the tenant as a prop.
  component: lazyRouteComponent(() =>
    import('./pages/admin/platform/PlatformTenantPage').then(({ default: PlatformTenantPage }) => ({
      default: function PlatformTenantRouteComponent() {
        const { tenantId } = adminPlatformTenantRoute.useParams();
        return <PlatformTenantPage tenantId={tenantId} />;
      },
    })),
  ),
});

// Every tenant's phone numbers, for Livepoint; each tenant opens its account page.
const adminPlatformNumbersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/platform/numbers',
  beforeLoad: requireRouteAccess('/admin/platform/numbers'),
  component: lazyRouteComponent(() => import('./pages/admin/platform/PlatformNumbersPage')),
});

const adminUnauthorizedRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  ...inAdminFrame,
  path: '/unauthorized',
  validateSearch: unauthorizedSearch,
  component: function UnauthorizedRouteComponent() {
    const { required, forbidden } = adminUnauthorizedRoute.useSearch();
    return <UnauthorizedPage required={required} forbidden={!!forbidden} />;
  },
});

// Self-service sign-up: dormant until the self-service sign-up rebuild; gated
// by SELF_SERVICE_SIGNUP_ENABLED on the server and its build-time mirror
// VITE_SELF_SERVICE_SIGNUP_ENABLED here. When VITE_SELF_SERVICE_SIGNUP_ENABLED !== 'true',
// the routes redirect to /admin/login (server-side /api/signup/* also 404s).
// Signing up is a purchase of opening credit, so the pages also need the
// server's runtime CREDIT_PURCHASES_ENABLED: while it is off the opening-credit
// list 404s and the pages show the ordinary not-found page. The routes stay registered so the bundle
// shape is unchanged.
const SELF_SERVICE_SIGNUP_ENABLED =
  (import.meta.env.VITE_SELF_SERVICE_SIGNUP_ENABLED as string | undefined) === 'true';

const blockSignupWhenDisabled = () => guardPublicPurchasePages(SELF_SERVICE_SIGNUP_ENABLED);

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-up',
  beforeLoad: blockSignupWhenDisabled,
  // `credits`: the opening credit a link names, a whole number of credits.
  validateSearch: z.object({ credits: positiveInt, canceled: str }),
  component: lazyRouteComponent(() => import('./pages/sign-up/SignUpPage')),
});

const signUpSuccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-up/success',
  beforeLoad: blockSignupWhenDisabled,
  validateSearch: z.object({ intent: str }),
  component: lazyRouteComponent(() => import('./pages/sign-up/SignUpSuccessPage')),
});

// ---------------------------------------------------------------------------
// Public password-reset pages (local-auth only).
// Children of rootRoute so they bypass requireAuth — the token in the URL
// IS the auth factor, and an unauthenticated user with a valid token must be
// able to reach the page. Paths are outside /admin/* so no inherited guard
// can apply by accident.
// ---------------------------------------------------------------------------

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: lazyRouteComponent(() => import('./pages/admin/ForgotPasswordPage')),
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  // `str`: a token of digits only is read as a number and must stay text.
  validateSearch: z.object({ token: str }),
  component: lazyRouteComponent(() => import('./pages/admin/ResetPasswordPage')),
});

// ---------------------------------------------------------------------------
// Catch-all — redirect to root
// ---------------------------------------------------------------------------

const catchAllRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '*',
  beforeLoad: () => {
    throw redirect({ to: '/' });
  },
});

// ---------------------------------------------------------------------------
// Route tree
// ---------------------------------------------------------------------------

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  signUpRoute,
  signUpSuccessRoute,
  chatRoute,
  adminLayoutRoute.addChildren([
    adminIndexRoute,
    adminDashboardRoute,
    adminWalkthroughRoute,
    adminUsersRoute,
    adminAgentsRoute,
    adminVoiceConfigsRoute,
    adminEmailTemplatesRoute,
    adminAnalyticsRoute,
    adminEvaluationRoute,
    adminAudioMonitorRoute,
    adminOutboundCallsRoute,
    adminConversationsRoute,
    adminIntegrationsRoute,
    adminPhoneNumbersRoute,
    adminAuditLogsRoute,
    adminApiKeysRoute,
    adminSystemRoute,
    adminSettingsRoute,
    adminWelcomeRoute,
    adminUsageRoute,
    adminUsageTopupRoute,
    adminBillingRoute,
    adminPlatformUsageRoute,
    adminPlatformCallQualityRoute,
    adminPlatformTenantUsageRoute,
    adminPlatformTenantsRoute,
    adminPlatformTenantRoute,
    adminPlatformNumbersRoute,
    adminUnauthorizedRoute,
  ]),
  catchAllRoute,
]);

// ---------------------------------------------------------------------------
// Router instance
// ---------------------------------------------------------------------------

export const router = createRouter({
  routeTree,
  // Text ids stay readable in the address (`?id=42`); see searchSerialization.
  parseSearch,
  stringifySearch,
  defaultPendingComponent: RoutePendingComponent,
  defaultErrorComponent: RouteErrorComponent,
  defaultNotFoundComponent: NotFoundPage,
  defaultPendingMs: 300,
  context: {
    queryClient: undefined!,
    auth: undefined!,
  },
});

// ---------------------------------------------------------------------------
// Type registration — enables full type safety across the app
// ---------------------------------------------------------------------------

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
